import { escapeHtml } from "@tellescope/utils";

import { findTokens } from "../tokens/recognize";
import { getTokenLabel } from "../tokens/tokenLabel";

/** A run of the HTML source: either literal source text or a single token (kept verbatim). */
export interface HtmlSegment {
  type: "text" | "token";
  value: string;
}

/**
 * Inline (iframe-safe) styling for token badges in the HTML preview. Tailwind classes and CSS
 * variables don't apply inside the sandboxed iframe, so this mirrors the `secondary` `Badge`
 * (`bg-muted` #f5f5f5 / `text-muted-foreground` #737373, rounded-md, px-2 py-0.5, text-xs medium)
 * with concrete values so the preview pills match the rich-text chips.
 */
const PREVIEW_BADGE_STYLE = [
  "display:inline-flex",
  "align-items:center",
  "white-space:nowrap",
  "border:1px solid transparent",
  "border-radius:0.5rem",
  "padding:0.125rem 0.5rem",
  "background:#f5f5f5",
  "color:#737373",
  "font-family:sans-serif",
  "font-size:0.75rem",
  "font-weight:500",
  "line-height:1rem",
].join(";");

/**
 * Render the HTML source for preview with each token replaced by a styled badge showing its
 * friendly label, so the user sees that those values will be filled in at send time (rather than a
 * raw `{{…}}` string). The surrounding HTML is left intact.
 */
export const decorateTokensAsBadges = (html: string): string =>
  htmlToSegments(html)
    .map((segment) =>
      segment.type === "text"
        ? segment.value
        : `<span style="${PREVIEW_BADGE_STYLE}">${escapeHtml(getTokenLabel(segment.value))}</span>`
    )
    .join("");

/**
 * Split an HTML source string into alternating literal-text and token segments, so the HTML editor
 * can render each token as a pill while keeping the surrounding raw HTML as editable text. Tokens
 * are preserved verbatim (`value` is the exact raw string).
 */
export const htmlToSegments = (html: string | undefined = ""): HtmlSegment[] => {
  const segments: HtmlSegment[] = [];
  let cursor = 0;
  findTokens(html).forEach(({ start, end, token }) => {
    if (start > cursor) segments.push({ type: "text", value: html.slice(cursor, start) });
    segments.push({ type: "token", value: token.raw });
    cursor = end;
  });
  if (cursor < html.length) segments.push({ type: "text", value: html.slice(cursor) });
  return segments;
};
